// SCR-STAFF-882 — اسناد (US-ACC-010 تا 014): فهرست اسناد تجمیعی روزانه با فیلتر بازه، شعبه، گروه و وضعیت خروجی؛ جزئیات سند با ردیف‌ها
// (حساب، تفصیلی‌ها، بدهکار و بستانکار، نقش‌ها) و Drill-down هر ردیف به ردیف‌های منبع و تراکنش اصلی (AC-ACC-012.1).
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type AccJournalExportStatus,
  type AccJournalLineSourceView,
  type AccJournalLineView,
  type AccJournalListItem,
  type AccPostingGroup,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  JalaliDatePickerRHF,
  KeyValueList,
  ListPage,
  LoadingState,
  StatusChip,
  type DataTableColumn,
} from '@phoenix/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useParams } from 'react-router';
import {
  accKeys,
  Amount,
  JournalExportChip,
  journalPath,
  LIST_SEPARATOR,
  num,
  SCR_STAFF_882,
  useAccBranches,
  useBranchName,
} from './common';
import { roleKey } from './MappingsPage';

const GROUPS: readonly AccPostingGroup[] = [
  'Sales',
  'Receipts',
  'Refunds',
  'Deferred',
  'Cash',
  'Insurance',
  'Compensation',
  'Inventory',
  'Expenses',
];
const EXPORT: readonly AccJournalExportStatus[] = ['NotExported', 'Exported', 'Failed'];

export function JournalsPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const branches = useAccBranches();
  const branchName = useBranchName();
  const { control, watch } = useForm<{ from: string | null; to: string | null }>({
    defaultValues: { from: null, to: null },
  });
  const from = watch('from');
  const to = watch('to');
  const [branchId, setBranchId] = useState('');
  const [group, setGroup] = useState<AccPostingGroup | ''>('');
  const [exportStatus, setExportStatus] = useState<AccJournalExportStatus | ''>('');
  const filter = [from, to, branchId, group, exportStatus].join('|');
  const journals = useInfiniteQuery({
    queryKey: accKeys.journals(session?.tenantId, filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/journals', {
          params: {
            query: {
              from: from ?? undefined,
              to: to ?? undefined,
              branchId: branchId || undefined,
              group: group || undefined,
              exportStatus: exportStatus || undefined,
              cursor: pageParam,
              limit: 50,
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const columns: DataTableColumn<AccJournalListItem>[] = [
    {
      id: 'number',
      headerKey: 'acc:journal.number',
      cell: (row) => (
        <Link component={RouterLink} to={journalPath(row.id)} data-testid="acc-journal-link">
          <bdi dir="ltr">{row.number}</bdi>
        </Link>
      ),
    },
    { id: 'date', headerKey: 'acc:common.date', cell: (row) => <JalaliDate value={row.businessDate} /> },
    { id: 'branch', headerKey: 'acc:common.branch', cell: (row) => branchName(row.branchId) },
    { id: 'group', headerKey: 'acc:journal.group', cell: (row) => t(`acc:group.${row.group}`) },
    { id: 'kind', headerKey: 'acc:journal.kind', cell: (row) => t(`acc:journal_kind.${row.kind}`) },
    { id: 'total', headerKey: 'acc:common.total', cell: (row) => <Amount money={row.total} /> },
    {
      id: 'status',
      headerKey: 'acc:common.status',
      cell: (row) => (
        <StatusChip
          tone={row.status === 'Posted' ? 'done' : 'cancelled'}
          labelKey={`acc:journal_status.${row.status}`}
        />
      ),
    },
    {
      id: 'export',
      headerKey: 'acc:journal.export_status',
      cell: (row) => <JournalExportChip status={row.exportStatus} />,
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_882}
      titleKey="acc:journal.title"
      subtitleKey="acc:journal.subtitle"
      filters={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <JalaliDatePickerRHF control={control} name="from" labelKey="acc:common.from" />
          <JalaliDatePickerRHF control={control} name="to" labelKey="acc:common.to" />
          <TextField
            select
            size="small"
            label={t('acc:common.branch')}
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('acc:common.all')}</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.branchId} value={branch.branchId}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('acc:journal.group')}
            value={group}
            onChange={(event) => setGroup(event.target.value as AccPostingGroup | '')}
            sx={{ minWidth: 160 }}
            slotProps={{ htmlInput: { 'data-testid': 'acc-journal-group' } }}
          >
            <MenuItem value="">{t('acc:common.all')}</MenuItem>
            {GROUPS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`acc:group.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('acc:journal.export_status')}
            value={exportStatus}
            onChange={(event) => setExportStatus(event.target.value as AccJournalExportStatus | '')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('acc:common.all')}</MenuItem>
            {EXPORT.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`acc:export_status.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      }
    >
      <DataTable
        captionKey="acc:journal.title"
        columns={columns}
        rows={journals.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={journals.isPending}
        error={journals.error}
        onRetry={() => void journals.refetch()}
        hasMore={journals.hasNextPage}
        onLoadMore={() => void journals.fetchNextPage()}
        isFetchingMore={journals.isFetchingNextPage}
        emptyTitleKey="acc:journal.empty"
      />
    </ListPage>
  );
}

export function JournalDetailPage() {
  const { t } = useTranslation('acc');
  const { id = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const branchName = useBranchName();
  const [line, setLine] = useState<AccJournalLineView | null>(null);
  const journal = useQuery({
    queryKey: accKeys.journal(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/journals/{id}', { params: { path: { id } } })),
  });
  const data = journal.data;
  const columns: DataTableColumn<AccJournalLineView>[] = [
    { id: 'no', headerKey: 'acc:common.level', cell: (row) => faDigits(num(row.lineNo)) },
    {
      id: 'account',
      headerKey: 'acc:journal.account',
      cell: (row) => (
        <span>
          <bdi dir="ltr">{row.accountCode}</bdi> {row.accountTitle}
        </span>
      ),
    },
    {
      id: 'details',
      headerKey: 'acc:journal.details',
      cell: (row) =>
        row.details.filter((detail) => detail !== null).length === 0
          ? '—'
          : row.details.map((detail, index) =>
              detail ? (
                <div key={`${detail.id}-${index}`}>
                  <bdi dir="ltr">{detail.code}</bdi> {detail.title}
                </div>
              ) : null,
            ),
    },
    {
      id: 'debit',
      headerKey: 'acc:journal.debit',
      cell: (row) => (num(row.debit.amount) > 0 ? <Amount money={row.debit} testId="acc-line-debit" /> : '—'),
    },
    {
      id: 'credit',
      headerKey: 'acc:journal.credit',
      cell: (row) =>
        num(row.credit.amount) > 0 ? <Amount money={row.credit} testId="acc-line-credit" /> : '—',
    },
    {
      id: 'roles',
      headerKey: 'acc:journal.roles',
      cell: (row) =>
        row.roles
          .split(LIST_SEPARATOR)
          .filter((role) => role)
          .map((role) => t(roleKey(role.trim()), { defaultValue: role.trim() }))
          .join(LIST_SEPARATOR),
    },
    {
      id: 'sources',
      headerKey: 'acc:journal.sources',
      cell: (row) => (
        <Button size="small" onClick={() => setLine(row)} data-testid="acc-line-drill">
          {faDigits(num(row.sourceCount))}
        </Button>
      ),
    },
  ];
  if (journal.isPending) {
    return (
      <ListPage screenId={SCR_STAFF_882} titleKey="acc:journal.crumb">
        <LoadingState rows={4} />
      </ListPage>
    );
  }
  if (journal.error || !data) {
    return (
      <ListPage screenId={SCR_STAFF_882} titleKey="acc:journal.crumb">
        <ErrorState error={journal.error} onRetry={() => void journal.refetch()} />
      </ListPage>
    );
  }
  const debit = data.lines.reduce((sum, row) => sum + num(row.debit.amount), 0);
  const credit = data.lines.reduce((sum, row) => sum + num(row.credit.amount), 0);
  return (
    <ListPage screenId={SCR_STAFF_882} titleKey="acc:journal.crumb">
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <KeyValueList
          columns={3}
          items={[
            {
              labelKey: 'acc:journal.number',
              value: (
                <bdi dir="ltr" data-testid="acc-journal-number">
                  {data.header.number}
                </bdi>
              ),
            },
            { labelKey: 'acc:common.date', value: <JalaliDate value={data.header.businessDate} /> },
            { labelKey: 'acc:common.branch', value: branchName(data.header.branchId) },
            { labelKey: 'acc:journal.group', value: t(`acc:group.${data.header.group}`) },
            { labelKey: 'acc:journal.kind', value: t(`acc:journal_kind.${data.header.kind}`) },
            {
              labelKey: 'acc:journal.export_status',
              value: <JournalExportChip status={data.header.exportStatus} />,
            },
            { labelKey: 'acc:journal.description', value: data.header.description },
            { labelKey: 'acc:common.total', value: <Amount money={data.header.total} /> },
            {
              labelKey: 'acc:journal.balanced',
              value: (
                <StatusChip
                  tone={debit === credit ? 'done' : 'error'}
                  labelKey={debit === credit ? 'acc:control.ok' : 'acc:control.mismatch'}
                />
              ),
            },
          ]}
        />
      </Paper>
      <DataTable
        captionKey="acc:journal.lines"
        columns={columns}
        rows={data.lines}
        getRowId={(row) => row.id}
        emptyTitleKey="acc:journal.empty"
      />
      {line ? <SourcesDialog journalId={data.header.id} line={line} onClose={() => setLine(null)} /> : null}
    </ListPage>
  );
}

function SourcesDialog({
  journalId,
  line,
  onClose,
}: {
  journalId: string;
  line: AccJournalLineView;
  onClose: () => void;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const sources = useQuery({
    queryKey: accKeys.sources(session?.tenantId, journalId, line.id),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/journals/{id}/lines/{lineId}/sources', {
          params: { path: { id: journalId, lineId: line.id } },
        }),
      ),
  });
  const columns: DataTableColumn<AccJournalLineSourceView>[] = [
    {
      id: 'date',
      headerKey: 'acc:journal.original_date',
      cell: (row) => <JalaliDate value={row.originalDate} />,
    },
    {
      id: 'document',
      headerKey: 'acc:journal.document',
      cell: (row) => <bdi dir="ltr">{row.documentNumber ?? row.documentType}</bdi>,
    },
    { id: 'event', headerKey: 'acc:journal.event', cell: (row) => <bdi dir="ltr">{row.eventType}</bdi> },
    { id: 'rule', headerKey: 'acc:journal.rule', cell: (row) => row.ruleId ?? '—' },
    {
      id: 'role',
      headerKey: 'acc:mapping.role',
      cell: (row) => t(roleKey(row.role), { defaultValue: row.role }),
    },
    {
      id: 'amount',
      headerKey: 'acc:common.amount',
      cell: (row) => <Amount money={row.amount} testId="acc-source-amount" />,
    },
  ];
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="acc-drill-title">
      <DialogTitle id="acc-drill-title">{t('acc:journal.drill_title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <bdi dir="ltr">{line.accountCode}</bdi> {line.accountTitle}
        </Typography>
        <DataTable
          captionKey="acc:journal.drill_title"
          columns={columns}
          rows={sources.data?.items}
          getRowId={(row) => `${row.sourceEntryId}-${row.role}-${row.parameter ?? ''}`}
          isLoading={sources.isPending}
          error={sources.error}
          onRetry={() => void sources.refetch()}
          emptyTitleKey="acc:journal.empty"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('acc:common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}
